import { useLocale, useTranslation } from '@multaqa/i18n'
import { localizedText } from '@/lib/me'
import { useWorkflowApproverMembers, useWorkflowApproverRoles } from './api'
import type { WorkflowStepDraft } from './workflow-levels'

/**
 * The display name of a level: "Direct manager", a role's name or a
 * person's name. One function, so the editor's summary and the HR path view
 * never name a level differently.
 */
export function useLevelName(companyId: string) {
  const { t } = useTranslation('workflow')
  const { locale } = useLocale()
  const { data: members = [] } = useWorkflowApproverMembers(companyId)
  const { data: roles = [] } = useWorkflowApproverRoles(companyId)

  return (step: WorkflowStepDraft): string | null => {
    switch (step.approver_type) {
      case 'direct_manager':
        return t('levels.directManager')
      case 'system_role':
        return t(`roles.systemRoles.${step.approver_system_role}`, { ns: 'common' })
      case 'role': {
        const role = roles.find((candidate) => candidate.id === step.approver_role_id)
        return role ? localizedText(role.name, locale) || role.key : null
      }
      case 'user': {
        const member = members.find((candidate) => candidate.user.id === step.approver_user_id)
        return member ? (member.user.name ?? member.user.email ?? null) : null
      }
      default:
        return null
    }
  }
}
